import { formatDate } from '@angular/common';

const LOCALE = 'en-US';
const DATE_ONLY_FORMAT = 'yyyy-MM-dd';
const YEAR_AND_MONTH_ONLY_FORMAT = 'yyyy-MM';
const DATE_TIME_FORMAT = "yyyy-MM-ddT00:00:00'Z'";

/**
 * Creates a Date object out of a full ISO UTC string.
 * The UTC part is removed in order to create a local Date with unchanged Date and Time
 * @param value A string date in the following format "yyyy-MM-ddTHH:mm:ssZ"
 * @returns A Date object or null
 * @throws Error param is not in full ISO UTC
 */
export const createDate = (value: string | null | undefined): Date | null => {
  if (!value) {
    return null;
  }

  validateFullISOInUTC(value);

  return new Date(value.replace('Z', ''));
};

/**
 * Formats a date in the following format: 'yyyy-MM-dd'.
 * Time and Time Zone Information are ignored.
 * @param value A Date object or date string
 * @returns A date string like '2026-07-23' or ''
 * @throws Error when param is string and not in full ISO UTC
 */
export const formatAsDateOnlyString = (value: Date | string | null | undefined): string => {
  return getDateString(value, DATE_ONLY_FORMAT);
};

/**
 * Formats a date in the following format: 'yyyy-MM'.
 * Time and Time Zone Information are ignored.
 * @param value A Date object or date string
 * @returns A date string like '2026-07' or ''
 * @throws Error when param is string and not in full ISO UTC
 */
export const formatAsYearAndMonthOnlyString = (value: Date | string | null | undefined): string => {
  return getDateString(value, YEAR_AND_MONTH_ONLY_FORMAT);
};

/**
 * Formats a date in the following format: 'yyyy-MM-ddTHH:mm:ssZ'.
 * Time and Time Zone Information are ignored.
 * @param value A Date object or date string
 * @returns A date string like '2026-07-23T00:00:00Z or ''
 * @throws Error when param is string and not in full ISO UTC
 */
export const formatAsDateTimeStringIgnoringTheTime = (value: Date | string | null | undefined): string => {
  return getDateString(value, DATE_TIME_FORMAT);
};

function getDateString(value: Date | string | null | undefined, format: string): string {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    validateFullISOInUTC(value);
    return formatDate(value.replace('Z', ''), format, LOCALE);
  }

  return formatDate(getDateTimeStringIgnoringTime(value), format, LOCALE);
}

/**
 * Creates a date string in the following format 'yyyy-MM-ddTHH:mm:ss'
 * @param value A Date object
 * @returns A string date like '2026-07-12T00:00:00'
 */
function getDateTimeStringIgnoringTime(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}T00:00:00`;
}

/**
 * Validate if param is in ISO UTC format like '2026-07-23T15:30:47Z'
 * @param value A date string
 * @throws Error when param is not in ISO UTC format 'yyyy-MM-ddTHH:mm:ssZ'
 */
function validateFullISOInUTC(value: string): void {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(value)) {
    throw Error(`Date string '${value}' is not in the correct format.`);
  }
}
